import { useFetcher, useLoaderData } from '@remix-run/react';
import { GQLHelpArticleFragment } from '@theprintbar/graphql';
import { WebRoutes, chunkArray, cn } from '@theprintbar/shared';
import React, { useEffect, useState } from 'react';
import { loader } from '~/routes/_layout/$/_index';
import { FAQModal } from './FAQModal';

interface QuestionButtonProps {
	question: GQLHelpArticleFragment;
	onClick: (question: GQLHelpArticleFragment) => void;
}

const QuestionButton: React.FC<QuestionButtonProps> = ({ question, onClick }) => (
	<button
		className="flex bg-blue-bright/20 border border-blue rounded-2xl p-2 lg:p-5 mr-2 lg:mr-4 w-fit items-center whitespace-nowrap hover:bg-blue-bright transition-all"
		onClick={() => onClick(question)}
	>
		<p className="font-black font-gravity tracking-wide lg:text-4xl flex-grow uppercase">{question.title}</p>
	</button>
);

const SkeletonButton: React.FC = () => (
	<div className="flex bg-grey/20 border border-grey animat-pulse rounded-2xl p-5 w-[40vw] items-center whitespace-nowrap mr-4">
		<p className="opacity-0 font-black font-gravity text-sm lg:text-4xl flex-grow uppercase">Loading...</p>
	</div>
);

export function FAQ() {
	const [isOpen, setIsOpen] = useState(false);
	const { helpArticles } = useLoaderData<typeof loader>();
	const questions: Array<GQLHelpArticleFragment | null> = helpArticles ?? Array(9).fill('');
	const [currentQuestion, setCurrentQuestion] = useState<GQLHelpArticleFragment | null>(null);

	const handleQuestionClick = (question: GQLHelpArticleFragment) => {
		setCurrentQuestion(question);
		setIsOpen(true);
	};

	const handleCloseModal = () => {
		setIsOpen(false);
		setCurrentQuestion(null); // Reset current question when closing the modal
	};

	return (
		<div className="flex flex-col gap-5 overflow-x-hidden">
			{chunkArray(questions, 3).map((questions, index) => {
				const buttons = questions.map((question, i) =>
					question ? (
						<QuestionButton key={question.id} question={question} onClick={() => handleQuestionClick(question)} />
					) : (
						<SkeletonButton key={`${index}${i}`} />
					),
				);

				return (
					<div key={`chunk-${index}`} className="flex gap-4 relative">
						<div className={cn('flex', index % 2 === 0 ? 'animate-marquee' : 'animate-marquee1_2')}>{buttons}</div>
						<div className={cn('flex absolute top-0', index % 2 === 0 ? 'animate-marquee2' : 'animate-marquee2_2')}>
							{buttons}
						</div>
					</div>
				);
			})}
			{!!currentQuestion && <FAQModal question={currentQuestion} isOpen={isOpen} setIsOpen={handleCloseModal} />}
		</div>
	);
}
